import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { login as loginRequest, logout as logoutRequest, refreshAccessToken } from "../api";

const AuthContext = createContext(null);
let bootstrapPromise = null;
let refreshPromise = null;

function decodeJwtPayload(token) {
    if (!token) {
        return null;
    }

    try {
        const parts = String(token).split(".");
        if (parts.length < 2) {
            return null;
        }

        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        return JSON.parse(window.atob(padded));
    } catch {
        return null;
    }
}

function mapAuthErrorMessage(error) {
    if (error?.code === "invalid_credentials") return "Email ou mot de passe invalide.";
    if (error?.code === "account_disabled") return "Compte désactivé.";
    if (error?.code === "missing_refresh_token") return "Session expirée.";
    if (error?.code === "refresh_token_expired") return "Session expirée, reconnecte-toi.";
    if (error?.code === "refresh_token_reuse_detected") return "Session compromise détectée. Reconnexion requise.";
    if (error?.code === "refresh_token_revoked") return "Session révoquée. Reconnexion requise.";
    return error?.message || "Une erreur est survenue.";
}

export function AuthProvider({ children }) {
    const [accessToken, setAccessToken] = useState(null);
    const [isBooting, setIsBooting] = useState(true);
    const [authMessage, setAuthMessage] = useState("");
    const currentUser = useMemo(() => {
        const payload = decodeJwtPayload(accessToken);
        if (!payload?.sub) {
            return null;
        }

        return {
            id: payload.sub,
            email: payload.email || "",
            role: String(payload.role || "").toLowerCase()
        };
    }, [accessToken]);
    const isAdmin = currentUser?.role === "admin";

    useEffect(() => {
        async function bootstrap() {
            if (!bootstrapPromise) {
                bootstrapPromise = refreshAccessToken().catch(() => null);
            }

            try {
                const data = await bootstrapPromise;
                setAccessToken(data?.accessToken || null);
            } finally {
                setIsBooting(false);
            }
        }

        bootstrap();
    }, []);

    async function login(email, password) {
        const data = await loginRequest(email, password);
        setAccessToken(data.accessToken || null);
        setAuthMessage("");
    }

    async function logout() {
        try {
            await logoutRequest();
        } finally {
            setAccessToken(null);
        }
    }

    async function refreshSession() {
        if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
                refreshPromise = null;
            });
        }

        const data = await refreshPromise;
        setAccessToken(data.accessToken || null);
        return data.accessToken;
    }

    async function withAuth(requestFn) {
        if (!accessToken) {
            throw Object.assign(new Error("Missing access token"), { code: "missing_access_token", status: 401 });
        }

        try {
            return await requestFn(accessToken);
        } catch (error) {
            if (error?.status !== 401) {
                throw error;
            }

            try {
                const refreshedToken = await refreshSession();
                return await requestFn(refreshedToken);
            } catch (refreshError) {
                await logout();
                setAuthMessage(mapAuthErrorMessage(refreshError));
                throw Object.assign(new Error("Session revoked"), { code: "session_revoked", status: 401, cause: refreshError });
            }
        }
    }

    const value = useMemo(() => ({
        accessToken,
        currentUser,
        isAdmin,
        isBooting,
        isAuthenticated: Boolean(accessToken),
        authMessage,
        setAuthMessage,
        login,
        logout,
        withAuth
    }), [accessToken, currentUser, isAdmin, isBooting, authMessage]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }

    return context;
}

export { mapAuthErrorMessage };
